import { User } from '@/types/app_types';

// ========================================
// 팝업 설정 타입
// ========================================

interface PopupOptions {
  url: string;
  name: string;
  width?: number;
  height?: number;
}

// ========================================
// 소셜 인증 메시지 타입 정의
// ========================================

/**
 * 임시 토큰과 함께 반환되는 사용자 정보 (회원가입 필요 시)
 */
interface TempUserData {
  tempToken: string;
  provider?: 'google' | 'kakao';
}

/**
 * Google/Kakao 인증 성공 시 Payload
 */
interface SocialAuthSuccessPayload {
  requiresAgreement: boolean;
  tempData?: TempUserData;
  user?: User;
  accessToken?: string;
  refreshToken?: string;
}

/**
 * 인증 실패 시 Payload
 */
interface SocialAuthErrorPayload {
  error: string;
  errorCode?: string;
}

/**
 * 인증 메시지 Union 타입
 */
export type SocialAuthMessage =
  | {
      type: 'GOOGLE_AUTH_SUCCESS' | 'KAKAO_AUTH_SUCCESS';
      payload: SocialAuthSuccessPayload;
    }
  | {
      type: 'GOOGLE_AUTH_ERROR' | 'KAKAO_AUTH_ERROR';
      payload: SocialAuthErrorPayload;
    }
  | {
      type: 'GOOGLE_AUTH_CANCELLED' | 'KAKAO_AUTH_CANCELLED';
      payload: Record<string, never>; // 빈 객체
    };

/**
 * 제네릭 인증 메시지 타입 (하위 호환성 유지)
 */
interface AuthMessage<T = unknown> {
  type: string;
  payload: T;
}

// ========================================
// 타입 가드 함수
// ========================================

/**
 * 성공 메시지인지 확인하는 타입 가드
 */
export function isAuthSuccessMessage(
  message: AuthMessage,
): message is Extract<SocialAuthMessage, { type: 'GOOGLE_AUTH_SUCCESS' | 'KAKAO_AUTH_SUCCESS' }> {
  return message.type === 'GOOGLE_AUTH_SUCCESS' || message.type === 'KAKAO_AUTH_SUCCESS';
}

/**
 * 에러 메시지인지 확인하는 타입 가드
 */
export function isAuthErrorMessage(
  message: AuthMessage,
): message is Extract<SocialAuthMessage, { type: 'GOOGLE_AUTH_ERROR' | 'KAKAO_AUTH_ERROR' }> {
  return message.type === 'GOOGLE_AUTH_ERROR' || message.type === 'KAKAO_AUTH_ERROR';
}

/**
 * 취소 메시지인지 확인하는 타입 가드
 */
export function isAuthCancelledMessage(
  message: AuthMessage,
): message is Extract<SocialAuthMessage, { type: 'GOOGLE_AUTH_CANCELLED' | 'KAKAO_AUTH_CANCELLED' }> {
  return message.type === 'GOOGLE_AUTH_CANCELLED' || message.type === 'KAKAO_AUTH_CANCELLED';
}

// ========================================
// 팝업 관련 함수
// ========================================

/**
 * 소셜 로그인 팝업 열기
 */
export const openAuthPopup = (options: PopupOptions): Window | null => {
  const { url, name, width = 500, height = 600 } = options;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  return window.open(
    url,
    name,
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=no,resizable=no,copyhistory=no`,
  );
};

/**
 * 소셜 인증 메시지 대기 (타입 안전성 개선)
 *
 * @param expectedTypes - 예상되는 메시지 타입 배열
 * @param timeout - 타임아웃 시간 (기본 60초)
 * @returns 인증 메시지 Promise
 *
 * @example
 * ```typescript
 * const message = await waitForAuthMessage(
 *   ['GOOGLE_AUTH_SUCCESS', 'GOOGLE_AUTH_ERROR', 'GOOGLE_AUTH_CANCELLED'],
 *   60000
 * );
 *
 * if (isAuthSuccessMessage(message)) {
 *   logger.debug('Success:', message.payload.requiresAgreement);
 * }
 * ```
 */
export const waitForAuthMessage = (expectedTypes: string[], timeout: number = 60000): Promise<SocialAuthMessage> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      reject(new Error('인증 시간이 초과되었습니다.'));
    }, timeout);

    const messageHandler = (event: MessageEvent) => {
      // origin 검증
      if (event.origin !== window.location.origin) return;

      // 메시지 구조 검증
      if (!event.data || typeof event.data !== 'object') return;
      if (!('type' in event.data) || !('payload' in event.data)) return;

      const message = event.data as SocialAuthMessage;

      if (expectedTypes.includes(message.type)) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
        resolve(message);
      }
    };

    window.addEventListener('message', messageHandler);
  });
};
